import { useMemo } from 'react';

import { useGetListInspection } from '../api/getInspectionList';
import { ContractState } from '../api/getShipment';

interface UseShipmentInspectionProps {
  shipment: any;
  enabled?: boolean;
}

interface FormattedShipmentData {
  shipmentWithInspection: any;
  telemetryProps: {
    geolocalisation?: { latitude: number; longitude: number };
    boxStatus?: string;
    contractState?: ContractState;
    lpui: string;
  };
  securityCodeProps: {
    lastInspectionStatus?: {
      status: string;
      date: string;
      time: string;
    };
    securityCode?: string;
  };
  assistanceProps: {
    lpui: string;
  };
}

/**
 * Custom hook to fetch and format shipment inspection data.
 * Encapsulates the logic for fetching inspection data and formatting it for display components.
 *
 * @param shipment - The shipment object
 * @param enabled - Whether to enable the query (default: true)
 * @returns Formatted shipment data with props for each section component
 */
export const useShipmentInspection = ({
  shipment,
  enabled = true,
}: UseShipmentInspectionProps): FormattedShipmentData => {
  const shipmentId = shipment?.shipment_id;
  const shouldFetch = !!shipmentId && enabled;

  const { data: inspectionData } = useGetListInspection({
    shipmentIds: shipmentId ? [shipmentId] : [],
    enabled: shouldFetch,
  });

  // Merge inspection data with shipment data
  const shipmentWithInspection = useMemo(
    () => ({ ...shipment, ...(inspectionData?.[0] || {}) }),
    [shipment, inspectionData]
  );

  // Format telemetry section props
  const telemetryProps = useMemo(
    () => ({
      geolocalisation:
        shipmentWithInspection?.position?.latitude &&
        shipmentWithInspection?.position?.longitude
          ? {
              latitude: shipmentWithInspection.position.latitude,
              longitude: shipmentWithInspection.position.longitude,
            }
          : undefined,
      boxStatus: shipmentWithInspection?.box_locked_status
        ? `BOX recorded as ${shipmentWithInspection.box_locked_status}`
        : 'BOX recorded as locked',
      contractState: shipmentWithInspection?.contract_state,
      lpui: shipmentWithInspection?.lp_ui,
    }),
    [shipmentWithInspection]
  );

  // Format security code section props
  const securityCodeProps = useMemo(() => {
    const getDateField = () => {
      const status = shipmentWithInspection?.status;
      if (status === 'ACTIVE' || status === 'REQUESTED')
        return shipmentWithInspection.requestedAt;
      if (status === 'USED') return shipmentWithInspection.startedAt;
      if (status === 'EXPIRED') return shipmentWithInspection.expiredAt;

      return shipmentWithInspection.requestedAt; // fallback
    };

    const dateField = getDateField();

    return {
      lastInspectionStatus: shipmentWithInspection?.status
        ? {
            status: shipmentWithInspection?.status || 'REQUESTED',
            date: new Date(dateField).toLocaleDateString(),
            time: new Date(dateField).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
          }
        : undefined,
      securityCode: shipmentWithInspection?.code,
    };
  }, [shipmentWithInspection]);

  // Format assistance section props
  const assistanceProps = useMemo(
    () => ({
      lpui: shipmentWithInspection?.lp_ui,
    }),
    [shipmentWithInspection]
  );

  return {
    shipmentWithInspection,
    telemetryProps,
    securityCodeProps,
    assistanceProps,
  };
};
